// Cliente HTTP mínimo para el API de DTB HUB. Inyecta el JWT y maneja 401.
import { avisar } from '../components/Dialogo';

const TOKEN_KEY = 'dtb_hub_token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Cuentas de prueba (hub_015): fuera de toda métrica salvo que quien usa la pantalla prenda "Incluir". La
// elección vive en este navegador y viaja en CADA petición (X-Incluir-Pruebas), así que vale en todas las pantallas.
const PRUEBAS_KEY = 'dtb_hub_incluir_pruebas';
export const incluyePruebas = (): boolean => { try { return localStorage.getItem(PRUEBAS_KEY) === '1'; } catch { return false; } };
export const setIncluyePruebas = (v: boolean) => { try { if (v) localStorage.setItem(PRUEBAS_KEY, '1'); else localStorage.removeItem(PRUEBAS_KEY); } catch { /* sin almacenamiento: queda como estaba */ } };

export class ApiError extends Error {
  status: number;
  /** El cuerpo de la respuesta (p. ej. `{ error, pin: 'incorrecto', quedan }` de un PIN que no pasó). */
  body?: any;
  constructor(status: number, message: string, body?: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function api<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  // Azure SWA altera el header Authorization; mandamos el token en x-auth-token
  // (header propio que pasa intacto). Authorization queda para dev local con `func`.
  if (token) {
    headers.set('x-auth-token', token);
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (incluyePruebas()) headers.set('x-incluir-pruebas', '1');
  const res = await fetch(`/api/${path.replace(/^\//, '')}`, { ...options, headers });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => ({})) : await res.text();

  if (!res.ok) {
    const b = isJson ? (body as any) : null;
    // La sesión del personal se cierra todos los días a las 3:00 a.m. (api/src/lib/sesion.ts): un 401 `sesion_vencida` se dice con el diálogo de la
    // app y se regresa al inicio de sesión (AuthContext registra qué hacer con `alVencerSesion`).
    if (res.status === 401 && token && b?.error === SESION_VENCIDA) {
      const msg = (typeof b.message === 'string' && b.message) || MENSAJE_SESION_VENCIDA;
      clearToken();
      sesionTerminada(msg);
      throw new ApiError(401, msg, b);
    }
    const msg = (isJson && (body as any)?.error) || `Error ${res.status}`;
    if (res.status === 401) clearToken();
    // Una sección que un admin cerró con la pantalla abierta (Administración → Permisos): AuthContext vuelve a pedir la
    // sesión y el menú deja de enseñarla.
    if (res.status === 403 && b?.codigo === 'seccion_cerrada') { try { alCerrar?.(); } catch { /* sin AuthContext */ } }
    throw new ApiError(res.status, msg, isJson ? body : undefined);
  }
  return body as T;
}

// ─────────────────────────────────────────── la sesión que se terminó (se cierra a las 3:00 a.m.)
export const SESION_VENCIDA = 'sesion_vencida';
export const MENSAJE_SESION_VENCIDA = 'Tu sesión terminó: se cierra todos los días a las 3:00 a.m. Vuelve a entrar.';
let alVencer: (() => void) | null = null;
let alCerrar: (() => void) | null = null;
/** Lo registra AuthContext: qué hacer cuando el API dice que una sección ya no se abre (volver a pedir la sesión). */
export function alCerrarseSeccion(f: (() => void) | null) { alCerrar = f; }
let avisando = false;
/** Lo registra AuthContext: qué hacer cuando la sesión se termina (salir y llevar al inicio de sesión). */
export function alVencerSesion(f: (() => void) | null) { alVencer = f; }
/** Varias peticiones a la vez con la sesión vencida: UN diálogo, y al cerrarlo, al inicio de sesión. */
function sesionTerminada(mensaje: string) {
  if (avisando) return;
  avisando = true;
  avisar({ titulo: 'Tu sesión terminó', mensaje, tipo: 'info', boton: 'Volver a entrar' })
    .finally(() => { avisando = false; try { alVencer?.(); } catch { /* sin AuthContext: el token ya se borró */ } });
}
